import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { amountToWordsSoles } from './amountToWords';
import { formatPrintDate } from './dateUtils';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = 14;

const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

function docTypeLabel(entity) {
  if (entity?.document_type === 'ruc') return 'RUC';
  if (entity?.document_type === 'dni') return 'DNI';
  if (entity?.document_type) return String(entity.document_type).toUpperCase();
  return 'Documento';
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(9);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
}

export async function downloadSalesContinuousServicePdf(service) {
  if (!service) {
    throw new Error('No hay datos del servicio para generar el PDF');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let y = MARGIN + 4;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...TEAL);
  doc.text('SERVICIO CONTINUO — VENTAS', pageWidth / 2, y, { align: 'center' });

  y += 4;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.7);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 8;

  const statusText = service.status === 'open' ? 'Abierto' : 'Cerrado';

  drawSegments(doc, MARGIN, y, [
    { text: 'Código:', style: 'label' },
    { text: service.code || '—', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Estado:', style: 'label' },
    { text: statusText, style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Inicio:', style: 'label' },
    { text: formatPrintDate(service.start_date) || '—', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fin:', style: 'label' },
    { text: service.end_date ? formatPrintDate(service.end_date) : 'En curso', style: 'value' },
  ]);

  y += 6;
  const clientLine = [
    { text: 'Cliente:', style: 'label' },
    { text: service.client?.name || 'N/A', style: 'value' },
  ];
  if (service.client?.document_number) {
    clientLine.push(
      { text: '·', style: 'sep' },
      { text: `${docTypeLabel(service.client)}:`, style: 'label' },
      { text: service.client.document_number, style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, clientLine);

  if (service.description) {
    y += 6;
    drawSegments(doc, MARGIN, y, [
      { text: 'Descripción:', style: 'label' },
      { text: String(service.description).slice(0, 120), style: 'value' },
    ]);
  }

  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...TEAL);
  doc.text('AVANCES', MARGIN, y);

  y += 6;
  const progress = [...(service.progress || [])].sort(
    (a, b) => String(a.progress_date).localeCompare(String(b.progress_date)),
  );

  const tableBody = progress.length
    ? progress.map((row) => [
        formatPrintDate(row.progress_date) || '—',
        row.description || '—',
        fmtQty(row.quantity),
        `S/ ${fmtMoney(row.unit_price)}`,
        `S/ ${fmtMoney(row.subtotal)}`,
        row.has_igv ? `${row.tax_rate || 18}%` : 'Exon.',
      ])
    : [['Sin avances registrados', '—', '—', '—', '—', '—']];

  autoTable(doc, {
    startY: y,
    head: [['Fecha', 'Descripción', 'Cant.', 'P. Unit', 'Subtotal', 'IGV']],
    body: tableBody,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 3,
      textColor: GRAY,
      lineColor: [229, 231, 235],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY_LIGHT);
      doc.text(
        `Servicio ${service.code || ''} — Pág. ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' },
      );
    },
  });

  const tableEndY = doc.lastAutoTable.finalY + 8;
  const amountsRightX = pageWidth - MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...TEAL);
  doc.text('Totales acumulados', MARGIN, tableEndY);

  let ty = tableEndY + 5;
  const lines = [
    ['Base imponible:', `S/ ${fmtMoney(service.base_imponible)}`],
    ['IGV:', `S/ ${fmtMoney(service.igv_amount)}`],
    ['Total:', `S/ ${fmtMoney(service.total_amount)}`],
  ];
  lines.forEach(([label, value], idx) => {
    doc.setFont('helvetica', idx === 2 ? 'bold' : 'normal');
    doc.setTextColor(...(idx === 2 ? TEAL : GRAY_MUTED));
    doc.text(label, pageWidth - MARGIN - 55, ty);
    doc.setTextColor(...GRAY);
    doc.text(value, amountsRightX, ty, { align: 'right' });
    ty += 5;
  });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_MUTED);
  doc.text(
    `Son: ${amountToWordsSoles(service.total_amount)}`,
    amountsRightX,
    ty + 2,
    { align: 'right' },
  );

  const safeCode = String(service.code || service.id || 'servicio').replace(/[^\w.-]+/g, '_');
  doc.save(`Servicio_Continuo_Ventas_${safeCode}.pdf`);
}
